import { DATE_FORMAT } from "common/constants";
import skipWords from "common/constants/skipWords";
import dayjs from "dayjs";
import { utils } from "xlsx";

export const roundCost = (cost) => Math.round(cost * 100) / 100;

export const checkPermission = (parsedRoleConfig, { config, permission }) =>
  parsedRoleConfig?.[config]?.indexOf(permission) > -1;

export const filterConfigElements = (roleConfig, elementsConfig = []) => {
  const userElements = elementsConfig
    .filter(
      (elementConfig) =>
        roleConfig?.[elementConfig.permission.key]?.indexOf(
          elementConfig.permission.value
        ) > -1
    )
    .map((elementConfig) => elementConfig.component);
  return userElements;
};

export const getAvailabilityText = (availability = "", supplier = {}) => {
  if (supplier.settings?.displayActualFigures) {
    return availability;
  }

  return availability > 0 ? "Yes" : "No";
};

export const getHalfSchemeQuantity = (
  orderedQuantity,
  availability,
  deal,
  free
) =>
  free % 2 === 0 ||
  (availability === orderedQuantity && orderedQuantity === Math.floor(deal / 2))
    ? orderedQuantity
    : orderedQuantity + 0.5;

export const getHalfSchemeFree = (
  orderedQuantity,
  availability,
  deal,
  free
) => {
  if (
    availability === orderedQuantity &&
    (orderedQuantity === Math.floor(deal / 2) ||
      (deal % 2 !== 0 &&
        free % 2 === 0 &&
        orderedQuantity === Math.ceil(deal / 2)))
  ) {
    return 0;
  }
  const schemeFreeQuantity = Math.floor(orderedQuantity / deal) * free;
  const halfSchemeFreeQuantity = free / 2;
  return schemeFreeQuantity + halfSchemeFreeQuantity;
};

export const getHalfSchemeText = (deal, supplier) => {
  if (!supplier) {
    return "--";
  }
  if (deal > 0) {
    return supplier?.settings?.usingHalfSchemes ? "Y" : "N";
  }
  return "--";
};

export const isValidQuantity = (
  quantity,
  { availability, deal = 0, free = 0, halfScheme = false }
) => {
  if (Number.isNaN(quantity)) {
    return false;
  }
  if (deal > 0) {
    if (halfScheme) {
      if (availability === quantity) {
        if (
          quantity === Math.floor(deal / 2) ||
          (deal % 2 !== 0 && free % 2 === 0 && quantity === Math.ceil(deal / 2))
        ) {
          return true;
        }
      }
      const hsQuantity = getHalfSchemeQuantity(
        quantity,
        availability,
        deal,
        free
      );
      const hsFree = getHalfSchemeFree(quantity, availability, deal, free);
      return hsQuantity + hsFree <= availability;
    }
    const freeQuantity = parseInt(quantity / deal, 10) * free;
    return freeQuantity + quantity <= availability;
  }
  return quantity <= availability;
};

export const isHalfSchemeApplicableWithoutSupplier = ({
  quantity,
  deal,
  free
}) => {
  if (Number.isNaN(quantity)) {
    return false;
  }
  if (deal === 0 || deal === 1) {
    return false;
  }
  const remaining = quantity % deal;
  if (deal % 2 !== 0 && free % 2 !== 0) {
    return remaining >= Math.floor(deal / 2);
  }
  return remaining >= deal / 2;
};

export const isHalfSchemeApplicable = ({ quantity, deal, free, supplier }) => {
  if (Number.isNaN(quantity)) {
    return false;
  }
  if (getHalfSchemeText(deal, supplier) !== "Y") {
    return false;
  }
  if (deal === 0 || deal === 1) {
    return false;
  }
  const remaining = quantity % deal;
  if (deal % 2 !== 0 && free % 2 !== 0) {
    return remaining >= Math.floor(deal / 2);
  }
  return remaining >= deal / 2;
};

export const getHalfSchemeValue = (deal, free) => {
  if (deal === 0 || deal === 1) {
    return 0;
  }
  if (deal % 2 !== 0 && free % 2 === 0) {
    return Math.ceil(deal / 2);
  }
  return Math.floor(deal / 2);
};

export const getValidQuantity = (
  quantity,
  { availability, deal, free, halfScheme = false }
) => {
  if (Number.isNaN(quantity)) {
    return 0;
  }
  if (deal > 0) {
    const batches = Math.floor(availability / (deal + free));
    const floorOrderQuantity = batches * deal;
    const totalBatchQuantity = batches * (deal + free);

    const remaining =
      availability - totalBatchQuantity >= deal
        ? deal - 1
        : availability - totalBatchQuantity;

    if (
      halfScheme &&
      isHalfSchemeApplicableWithoutSupplier({
        quantity: remaining,
        deal,
        free
      }) &&
      getHalfSchemeQuantity(remaining, availability, deal, free) +
        getHalfSchemeFree(remaining, availability, deal, free) +
        totalBatchQuantity >
        availability
    ) {
      return floorOrderQuantity + getHalfSchemeValue(deal, free) - 1;
    }

    return floorOrderQuantity + remaining;
  }
  return availability;
};

export const isHalfSchemeEnabled = (deal, supplier) => {
  if (getHalfSchemeText(deal, supplier) !== "Y") {
    return false;
  }
  return true;
};

export const getProductQuantityFromCart = (
  productId,
  supplier,
  cartDetails = []
) => {
  if (!productId || !supplier) {
    return 0;
  }
  const productInCart = cartDetails
    .filter((cartItems) => cartItems.supplier.id === supplier.id)
    .flatMap((cartItems) => cartItems.inventory)
    .filter(
      (cartProduct) => cartProduct.inventoryAvailability.id === productId
    );

  return productInCart.length > 0 ? productInCart[0].orderedQuantity : 0;
};

export const getDiscountQuantityFromCart = (
  productId,
  supplier,
  cartDetails = []
) => {
  if (!supplier) {
    return 0;
  }
  const productInCart = cartDetails
    .filter((cartItems) => cartItems.supplier.id === supplier.id)
    .flatMap((cartItems) => cartItems.inventory)
    .filter(
      (cartProduct) => cartProduct.inventoryAvailability.id === productId
    );

  return productInCart.length > 0 ? productInCart[0]?.discount || 0 : 0;
};

export const getColor = (available) => {
  if (available === "YES") {
    return "green";
  }
  if (available === "No") {
    return "red";
  }

  if (available > 100) {
    return "green";
  }
  if (available > 10 && available <= 100) {
    return "blue";
  }
  if (available <= 10) {
    return "red";
  }
  return "";
};

export const getSpecialFreeQuantityFromCart = (
  productId,
  supplier,
  cartDetails = []
) => {
  if (!supplier) {
    return 0;
  }
  const productInCart = cartDetails
    .filter((cartItems) => cartItems.supplier.id === supplier.id)
    .flatMap((cartItems) => cartItems.inventory)
    .filter(
      (cartProduct) => cartProduct.inventoryAvailability.id === productId
    );

  return productInCart.length > 0
    ? productInCart[0]?.specialFreeQuantity || 0
    : 0;
};

export const getUserRole = (authConfig) => authConfig?.user?.role?.role;

export const formatDate = (date, format) => {
  switch (format) {
    case DATE_FORMAT.standard_dmy: {
      return new Date(date).toLocaleDateString("en-GB"); // GB for british
    }
    default:
      return date;
  }
};

export const validateNumberInput = (evt) =>
  [".", "e", "E", "-", "+"].indexOf(evt.key) > -1 && evt.preventDefault();

export const validateFloatNumberInput = (evt) =>
  ["e", "E", "-", "+"].indexOf(evt.key) > -1 && evt.preventDefault();

export const debounce = (func, delay) => {
  let timeoutId;

  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export const formatNames = (name = "") => {
  // to lowerCase
  let modifiedString = name;

  modifiedString = name.toLowerCase();
  // replace - , ^ with single space
  modifiedString = modifiedString.replace(/[\^-]/g, " ");
  modifiedString = modifiedString.replace(/[.]/g, " ");
  modifiedString = modifiedString.replace(/[&]/g, " ");
  modifiedString = modifiedString.replace(/[%]/g, " ");
  modifiedString = modifiedString.replace(/\*/g, " ");
  // replace all extra spaces to single space
  modifiedString = modifiedString.replace(/  +/g, "");

  modifiedString = modifiedString.replace(/ /g, "");

  return modifiedString.trimStart();
};

export const formatQuery = (name = "") => {
  if (name === null) {
    return null;
  }
  let modifiedString = name.toString().toLowerCase();

  modifiedString = modifiedString.replace(/[^a-z0-9]/g, "");

  return modifiedString;
};

export const hasSearchText = (searchText = "", ...names) => {
  const formatSearchText = formatQuery(searchText);
  for (const name of names) {
    const formatName = formatQuery(name);
    if (formatName?.includes(formatSearchText)) {
      return true;
    }
  }
  return false;
};

export const numberOfDaysToToday = (date) => {
  const minutesPerDay = 1000 * 60 * 60 * 24;
  const currentDate = new Date();
  const invoiceDate = new Date(date);
  const currentDateTime = Date.UTC(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate()
  );
  const invoiceDateTime = Date.UTC(
    invoiceDate.getFullYear(),
    invoiceDate.getMonth(),
    invoiceDate.getDate()
  );
  return Math.floor((currentDateTime - invoiceDateTime) / minutesPerDay);
};

export const getAuthorizedPlaceOrderTabs = (authConfig) =>
  authConfig?.roleConfig?.transactionModes
    ?.find((transactionMode) => transactionMode.name === authConfig.currentMode)
    ?.features?.find((feature) => feature?.name === "Cart")
    ?.permissionTypes?.find(
      (permissionType) => permissionType?.name === "Create"
    )
    ?.placeOrderTypePermissions?.map(
      (placeOrderTypePermission) => placeOrderTypePermission?.type
    );

export const getFormattedValue = (value = 0) =>
  Number(parseFloat(value).toFixed(2)).toLocaleString();

export const downloadCSV = (data, title = "sample file") => {
  const worksheet = utils.aoa_to_sheet(data);
  const csvData = utils.sheet_to_csv(worksheet);
  const blob = new Blob([csvData], {
    type: "text/csv;charset=utf-8;"
  });

  // Create a download link and trigger a click event to download the CSV file
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", `${title}.csv`);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getFilteredProductName = (productName) => {
  if (!productName) {
    return "";
  }
  const updatedSkipWords = skipWords.map((word) =>
    word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  // regex pattern to match any of the skip words, case-insensitive
  const pattern = new RegExp(
    `(^|\\s)(${updatedSkipWords.join("|")})(?=\\s|$)`,
    "gi"
  );
  // Replace any occurrence of the skip words with an empty string
  let filteredName = productName;
  while (pattern.test(filteredName)) {
    filteredName = filteredName.replace(pattern, "");
  }
  return filteredName.trim().replace(/\s+/g, " ");
};

export const generateSlug = (title) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

export const getShopCloseMessage = (shopCloseFrom, shopCloseTo) => {
  let message = "";

  const isShopCloseDateExpired = dayjs().isAfter(shopCloseFrom);
  const isShopOpenDateExpired = dayjs().isAfter(shopCloseTo);

  if (isShopOpenDateExpired) {
    return message;
  }

  const shopCloseDate = isShopCloseDateExpired
    ? dayjs().startOf("day")
    : dayjs(shopCloseFrom);
  const shopOpenDate = dayjs(shopCloseTo);

  const showCloseTime = shopCloseDate.hour() === 0;
  const showOpenTime = shopOpenDate.hour() === 0;

  const today = dayjs();
  const tomorrow = dayjs().add(1, "day");

  const isShopClosedToday = shopCloseDate.isSame(today, "date");
  const isToday =
    shopCloseDate.isSame(today, "date") && shopOpenDate.isSame(today, "date");

  const isTomorrow =
    shopCloseDate.isSame(tomorrow, "date") &&
    shopOpenDate.isSame(tomorrow, "date");

  const isSameDate = shopCloseDate.isSame(shopOpenDate, "date");

  const fullDayClose =
    dayjs(shopOpenDate).diff(dayjs(shopCloseDate), "hour") >= 23;

  const formatTime = (t) => t.format("hh A");
  const formatDateMonth = (d) => d.format("D MMM");
  const formatFull = (d) => d.format("D MMM hh A");

  if (isToday && fullDayClose) {
    message = "Closed Today";
  } else if (isToday && !fullDayClose) {
    message = `Closed today from ${formatTime(shopCloseDate)} to ${formatTime(shopOpenDate)}`;
  } else if (isTomorrow && (fullDayClose || (showCloseTime && showOpenTime))) {
    message = "Closed Tomorrow";
  } else if (isTomorrow && !fullDayClose) {
    message = `Closed Tomorrow from ${formatTime(shopCloseDate)} to ${formatTime(shopOpenDate)}`;
  } else if (isSameDate && (fullDayClose || (showCloseTime && showOpenTime))) {
    message = `Closed  on ${dayjs(shopCloseDate).format("MMM DD")} `;
  } else if (isShopClosedToday) {
    message = `Closed from  Today to ${showCloseTime ? formatDateMonth(shopOpenDate) : formatFull(shopOpenDate)}`;
  } else if (isSameDate && !fullDayClose) {
    message = `Closed  on ${formatDateMonth(shopCloseDate)} from ${formatTime(shopCloseDate)} to  ${formatTime(shopOpenDate)}`;
  } else {
    message = `Closed from  ${showCloseTime ? formatDateMonth(shopCloseDate) : formatFull(shopCloseDate)} to ${showOpenTime ? formatDateMonth(shopOpenDate) : formatFull(shopOpenDate)}`;
  }

  return message;
};

export const getShopCloseDates = (preferences) => {
  let shopCloseDate;
  let shopOpenDate;
  let shopCloseMessage = "";

  for (const pref of preferences) {
    if (
      pref?.preferenceDefault?.feature?.name === "shop-close-date" &&
      pref?.isActive
    ) {
      shopCloseDate = pref?.value?.split("&")[0];
      shopOpenDate = pref?.value?.split("&")[1];
    }
  }

  if (shopCloseDate && shopOpenDate) {
    shopCloseMessage = getShopCloseMessage(shopCloseDate, shopOpenDate);
  }
  return shopCloseMessage;
};
